import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import ImageManager from '../../components/admin/ImageManager';
import { PromoBanner } from '../../components/PromoBanner';
import {
  API, withCreds, inputCls, selectCls, btnPrimary, btnSecondary, Field, Modal, PageHeader, Spinner,
  ScheduleBadge, discountLabel, fmtDateTime, toLocalInput, fromLocalInput, errMsg,
} from '../../components/admin/AdminUI';
import { Plus, Edit, Trash2, Megaphone } from 'lucide-react';
import Select from '../../components/FlatSelect';

const PLACEMENTS = [
  ['top_bar', 'Top bar', 'Slim strip above the menu on every page'],
  ['home', 'Home page', 'Large banner near the top of the home page'],
  ['product', 'Product pages', 'Under the price, only on items the sale or coupon covers'],
  ['cart', 'Cart', 'Next to the order summary'],
];
const THEMES = [['#FFD700', '#000000'], ['#00E5FF', '#000000'], ['#EF4444', '#FFFFFF'], ['#111827', '#FFFFFF'], ['#22C55E', '#000000'], ['#A855F7', '#FFFFFF']];

function PromoModal({ promo, sales, coupons, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', headline: '', message: '', cta_text: 'Shop now', cta_link: '', image: '', bg_color: '#FFD700', text_color: '#000000',
    placements: ['top_bar'], sale_id: '', coupon_code: '', show_countdown: true, starts_at: null, ends_at: null, active: true, sort_order: 0,
    ...promo,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const sale = sales.find(s => s.id === form.sale_id);
  const coupon = coupons.find(c => c.code === form.coupon_code);

  const linkSale = (id) => {
    const s = sales.find(x => x.id === id);
    setForm(f => ({ ...f, sale_id: id, ...(s && !f.headline ? { headline: `${s.name}: ${discountLabel(s.discount_type, s.discount_value)}` } : {}) }));
  };
  const linkCoupon = (code) => {
    const c = coupons.find(x => x.code === code);
    setForm(f => ({ ...f, coupon_code: code, ...(c && !f.headline ? { headline: `${discountLabel(c.type, c.value)} with code ${c.code}` } : {}) }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const { id, status, created_at, updated_at, ...body } = form;
    try {
      if (promo?.id) await axios.put(`${API}/admin/promotions/${promo.id}`, body, withCreds);
      else await axios.post(`${API}/admin/promotions`, body, withCreds);
      onSaved();
    } catch (err) { setError(errMsg(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  const preview = {
    ...form, coupon_code: form.coupon_code,
    countdown_to: form.show_countdown ? (sale?.end_date || form.ends_at || coupon?.expires_at) : null,
    cta_link: form.cta_link || (form.sale_id ? '/sale' : ''),
  };

  return (
    <Modal title={promo?.id ? 'Edit Promotion' : 'New Promotion'} onClose={onClose} width="max-w-3xl">
      <form onSubmit={save} className="space-y-5">
        <div className="space-y-3">
          <p className="text-ink/55 text-xs uppercase tracking-widest">Preview</p>
          <div className="rounded-xl overflow-hidden"><PromoBanner promo={preview} variant="bar" /></div>
          <PromoBanner promo={preview} variant="card" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Advertise a sale" hint="Shows only while the sale is live">
            <Select value={form.sale_id} onChange={e => linkSale(e.target.value)} className={`${selectCls} w-full`}>
              <option value="">None</option>
              {sales.map(s => <option key={s.id} value={s.id}>{s.name} ({s.status})</option>)}
            </Select>
          </Field>
          <Field label="Show a coupon code" hint="Shoppers can tap to copy it">
            <Select value={form.coupon_code} onChange={e => linkCoupon(e.target.value)} className={`${selectCls} w-full`}>
              <option value="">None</option>
              {coupons.map(c => <option key={c.id} value={c.code}>{c.code}: {discountLabel(c.type, c.value)} ({c.status})</option>)}
            </Select>
          </Field>
          <Field label="Headline *" className="col-span-2">
            <input value={form.headline} onChange={e => set('headline', e.target.value)} required className={inputCls} />
          </Field>
          <Field label="Message" className="col-span-2">
            <input value={form.message} onChange={e => set('message', e.target.value)} placeholder="e.g. Ends Sunday, no code needed" className={inputCls} />
          </Field>
          <Field label="Button text"><input value={form.cta_text} onChange={e => set('cta_text', e.target.value)} className={inputCls} /></Field>
          <Field label="Button link" hint={form.sale_id ? 'Blank = the Sale page' : 'e.g. /pages/gift-guide or a full URL'}>
            <input value={form.cta_link} onChange={e => set('cta_link', e.target.value)} className={inputCls} />
          </Field>
        </div>

        <Field label="Colors">
          <div className="flex flex-wrap items-center gap-2">
            {THEMES.map(([bg, fg]) => (
              <button type="button" key={bg} onClick={() => setForm(f => ({ ...f, bg_color: bg, text_color: fg }))}
                className={`w-8 h-8 rounded-full border-2 ${form.bg_color === bg ? 'border-ink ring-2 ring-white' : 'border-ink/10'}`} style={{ backgroundColor: bg }} />
            ))}
            <label className="text-ink/55 text-xs ml-3 flex items-center gap-1">Background <input type="color" value={form.bg_color} onChange={e => set('bg_color', e.target.value)} /></label>
            <label className="text-ink/55 text-xs flex items-center gap-1">Text <input type="color" value={form.text_color} onChange={e => set('text_color', e.target.value)} /></label>
          </div>
        </Field>

        <Field label="Image (optional, used on larger banners)">
          <ImageManager images={form.image ? [form.image] : []} onChange={imgs => set('image', imgs[imgs.length - 1] || '')} multiple={false} subfolder="promotions" />
        </Field>

        <Field label="Show it on">
          <div className="grid grid-cols-2 gap-2">
            {PLACEMENTS.map(([k, l, d]) => (
              <label key={k} className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer ${form.placements.includes(k) ? 'border-honey/40 bg-honey/5' : 'border-ink/10'}`}>
                <input type="checkbox" checked={form.placements.includes(k)} className="accent-honey mt-1"
                  onChange={e => set('placements', e.target.checked ? [...form.placements, k] : form.placements.filter(p => p !== k))} />
                <span><span className="text-ink text-sm block">{l}</span><span className="text-ink/55 text-xs">{d}</span></span>
              </label>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Start showing" hint="Blank = now">
            <input type="datetime-local" value={toLocalInput(form.starts_at)} onChange={e => set('starts_at', fromLocalInput(e.target.value))} className={inputCls} style={{ colorScheme: 'light' }} />
          </Field>
          <Field label="Stop showing" hint={form.sale_id ? 'Blank = when the sale ends' : 'Blank = until turned off'}>
            <input type="datetime-local" value={toLocalInput(form.ends_at)} onChange={e => set('ends_at', fromLocalInput(e.target.value))} className={inputCls} style={{ colorScheme: 'light' }} />
          </Field>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.show_countdown} onChange={e => set('show_countdown', e.target.checked)} className="accent-honey" /><span className="text-ink/75 text-sm">Show a countdown timer</span></label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="accent-honey" /><span className="text-ink/75 text-sm">Enabled</span></label>
          <label className="flex items-center gap-2 text-ink/60 text-sm">Order <input type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)} className="w-16 bg-ink/[0.04] border border-ink/10 rounded-lg px-2 py-1 text-ink" /></label>
        </div>

        {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>{saving ? 'Saving…' : 'Save promotion'}</button>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

export default function AdminPromotions() {
  const [params, setParams] = useSearchParams();
  const [promos, setPromos] = useState([]);
  const [sales, setSales] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [p, s, c] = await Promise.all([
        axios.get(`${API}/admin/promotions`, withCreds), axios.get(`${API}/admin/sales`, withCreds), axios.get(`${API}/admin/coupons`, withCreds),
      ]);
      setPromos(p.data.promotions); setSales(s.data.sales); setCoupons(c.data.coupons);
      return { sales: s.data.sales, coupons: c.data.coupons };
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load().then(({ sales: ss, coupons: cs }) => {
      // Arriving from the megaphone button on a sale or coupon: start a promo for it
      const saleId = params.get('sale'), code = params.get('coupon');
      const s = ss.find(x => x.id === saleId), c = cs.find(x => x.code === code);
      if (s) setEditing({ sale_id: s.id, headline: `${s.name}: ${discountLabel(s.discount_type, s.discount_value)}` });
      else if (c) setEditing({ coupon_code: c.code, headline: `${discountLabel(c.type, c.value)} with code ${c.code}` });
      if (saleId || code) setParams({}, { replace: true });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.headline}"?`)) return;
    await axios.delete(`${API}/admin/promotions/${p.id}`, withCreds);
    load();
  };

  const linked = (p) => [p.sale_id && `Sale: ${sales.find(s => s.id === p.sale_id)?.name || '(deleted)'}`, p.coupon_code && `Code ${p.coupon_code}`].filter(Boolean).join(' · ');

  return (
    <AdminLayout>
      {editing && <PromoModal promo={editing === 'new' ? {} : editing} sales={sales} coupons={coupons} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <PageHeader title="Promotions" subtitle="Banners that advertise your sales and coupons around the store">
        <button onClick={() => setEditing('new')} className={btnPrimary}><Plus className="w-4 h-4" /> New Promotion</button>
      </PageHeader>
      <div className="admin-card rounded-xl overflow-x-auto">
        {loading ? <Spinner /> : promos.length === 0 ? (
          <div className="text-center py-12"><Megaphone className="w-8 h-8 text-ink/30 mx-auto mb-2" /><p className="text-ink/45 text-sm">No promotions yet. Tip: use the megaphone button on a sale or coupon.</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10">
              <tr className="text-ink/55 text-xs uppercase tracking-widest">
                <th className="text-left p-3">Promotion</th><th className="text-left p-3">Linked to</th><th className="text-left p-3">Shows on</th>
                <th className="text-left p-3">Schedule</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10">
              {promos.map(p => (
                <tr key={p.id} className="text-ink/75 hover:bg-ink/[0.04]">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: p.bg_color }} />
                      <span className="text-ink font-medium">{p.headline}</span>
                    </div>
                  </td>
                  <td className="p-3 text-xs">{linked(p) || '—'}</td>
                  <td className="p-3 text-xs">{p.placements.map(k => PLACEMENTS.find(x => x[0] === k)?.[1]).join(', ')}</td>
                  <td className="p-3 text-xs text-ink/60">{p.starts_at ? fmtDateTime(p.starts_at) : 'Now'} → {p.ends_at ? fmtDateTime(p.ends_at) : p.sale_id ? 'sale end' : 'no end'}</td>
                  <td className="p-3"><ScheduleBadge status={p.status} /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => setEditing(p)} className="text-ink/55 hover:text-honey-700"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => remove(p)} className="text-ink/55 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-ink/45 text-xs mt-3">A promotion linked to a sale or coupon hides itself automatically when that sale ends or the coupon expires or runs out.</p>
    </AdminLayout>
  );
}
